import React, { useState, useEffect, useContext } from "react";
import { 
  Gift, CreditCard, RefreshCw, FileText, ArrowRight, CheckCircle, 
  Clock, Landmark, PlusCircle, HelpCircle, ShieldAlert 
} from "lucide-react";
import { employeeWorkforceAPI } from "../../api/apiService";
import { NotificationContext } from "../../context/NotificationContext";
import EmptyState from "../../components/EmptyState";

export default function IncentivesPayrollPage() {
  const { addNotification } = useContext(NotificationContext);
  const [payrolls, setPayrolls] = useState([]);
  const [dashData, setDashData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPayrollAndStats = async () => {
    setLoading(true);
    try {
      const [payRes, dashRes] = await Promise.all([
        employeeWorkforceAPI.getPayrolls(),
        employeeWorkforceAPI.getDashboard()
      ]);
      setPayrolls(payRes.data);
      setDashData(dashRes.data);
    } catch (err) {
      console.error(err);
      addNotification("Failed to fetch payroll history", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollAndStats();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900">
        <EmptyState
          preset="payroll"
          title="Loading Payroll Incentives..."
          message="Please wait a moment."
        />
      </div>
    );
  }

  const statusBadges = {
    "PENDING": "bg-amber-50 text-amber-600 dark:bg-amber-950/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800",
    "PAID": "bg-green-50 text-green-600 dark:bg-green-950/30 dark:text-green-400 border border-green-200 dark:border-green-800",
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-slate-900 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        
        {/* Header */}
        <div className="bg-white dark:bg-slate-800 p-6 md:p-8 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-600 rounded-2xl text-white shadow-lg shadow-green-500/20">
              <Gift className="w-8 h-8" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 dark:text-white">Salary & Incentives</h1>
              <p className="text-gray-500 dark:text-slate-400 font-medium">Estimate monthly incentives, view current commission slabs, and access payment slips</p>
            </div>
          </div>
        </div>

        {/* Top Cards: Current month estimation */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          
          {/* Current Commission Slabs card */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Current Active Commission Slab</h3>
              <p className="text-3xl font-black text-green-600 dark:text-green-400">
                {dashData?.incentive_percentage || 0}% <span className="text-sm text-gray-400 font-medium">commission rate</span>
              </p>
              <p className="text-xs text-gray-400 mt-2 font-medium">
                Based on your monthly sales volume of <span className="font-bold text-gray-700 dark:text-slate-200">₹{dashData?.revenue_month?.toLocaleString() || 0}</span>.
              </p>
            </div>
            
            <div className="mt-6 space-y-3">
              <SlabRow label="₹0 to ₹50k Sales" rate="0%" active={!dashData || dashData.revenue_month < 50000} />
              <SlabRow label="₹50k to ₹100k Sales" rate="2%" active={dashData && dashData.revenue_month >= 50000 && dashData.revenue_month < 100000} />
              <SlabRow label="₹100k to ₹500k Sales" rate="5%" active={dashData && dashData.revenue_month >= 100000 && dashData.revenue_month < 500000} />
              <SlabRow label="₹500k+ Sales" rate="10%" active={dashData && dashData.revenue_month >= 500000} />
            </div>
          </div>

          {/* Current Month Estimated Commission */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Estimated Incentives (This Month)</h3>
              <p className="text-3xl font-black text-gray-900 dark:text-white">
                ₹{dashData?.incentives_earned?.toLocaleString() || 0}
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-2 font-medium">
                Commission calculation is processed in real time when you issue a completed POS invoice.
              </p>
            </div>
            
            <div className="bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900/50 p-4 rounded-2xl mt-6 space-y-2">
              <div className="flex justify-between text-xs font-bold text-green-700 dark:text-green-400">
                <span>Month Sales Volume:</span>
                <span>₹{dashData?.revenue_month?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between text-xs font-bold text-green-700 dark:text-green-400">
                <span>Commission Rate:</span>
                <span>{dashData?.incentive_percentage}%</span>
              </div>
              <div className="border-t border-green-200/50 dark:border-green-800/50 pt-2 flex justify-between text-xs font-black text-green-800 dark:text-green-300">
                <span>Est. Payout:</span>
                <span>₹{dashData?.incentives_earned?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Target card */}
          <div className="bg-white dark:bg-slate-800 p-6 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm flex flex-col justify-between">
            <div>
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Monthly Sales Target</h3>
              <p className="text-3xl font-black text-gray-900 dark:text-white">
                {dashData?.target_achievement_pct?.toFixed(0)}% <span className="text-sm text-gray-400 font-medium">Achieved</span>
              </p>
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-2 font-medium">
                Goal: <span className="font-bold text-gray-700 dark:text-slate-200">₹{dashData?.monthly_target?.toLocaleString() || 0}</span> volume.
              </p>
            </div>

            <div className="space-y-3 mt-6">
              <div className="w-full bg-gray-100 dark:bg-slate-900 rounded-full h-3 overflow-hidden border border-gray-200/50 dark:border-slate-800">
                <div 
                  className="h-full bg-green-500 rounded-full"
                  style={{ width: `${dashData?.target_achievement_pct || 0}%` }}
                />
              </div>
              <div className="flex justify-between text-[10px] font-black text-gray-400 uppercase tracking-widest">
                <span>₹0</span>
                <span>₹{dashData?.revenue_month?.toLocaleString()} Achieved</span>
              </div>
            </div>
          </div>

        </div>

        {/* Payroll History */}
        <div className="bg-white dark:bg-slate-800 rounded-3xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-50 dark:border-slate-700">
            <h2 className="text-lg font-black text-gray-900 dark:text-white uppercase tracking-wider">
              Payroll History & Slips
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-50 dark:bg-slate-900/50 border-b border-gray-100 dark:border-slate-700">
                <tr>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Pay Month</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Basic Salary</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Sales Incentives</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Deductions (Tax/PF/LOP)</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Net Paid Amount</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider">Status</th>
                  <th className="px-6 py-4 text-xs font-bold uppercase text-gray-400 tracking-wider text-right">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-700 text-sm">
                {payrolls.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-400">
                      No payroll logs found. Contact owner to generate payroll reports.
                    </td>
                  </tr>
                ) : payrolls.map(item => {
                  const deductions = parseFloat(item.pf) + parseFloat(item.tax) + parseFloat(item.loss_of_pay);
                  const allowances = parseFloat(item.bonus) + parseFloat(item.travel_allowance);
                  
                  return (
                    <tr key={item.id} className="hover:bg-gray-50/50 dark:hover:bg-slate-800/30 transition-colors">
                      <td className="px-6 py-4 font-bold text-gray-900 dark:text-white">
                        {item.month}
                      </td>
                      <td className="px-6 py-4 text-gray-600 dark:text-slate-400">
                        ₹{parseFloat(item.basic_salary).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-green-600 dark:text-green-400 font-medium">
                        +₹{parseFloat(item.sales_incentive).toLocaleString()}
                      </td>
                      <td className="px-6 py-4 text-red-500 font-medium">
                        -₹{deductions.toLocaleString()}
                        {parseFloat(item.loss_of_pay) > 0 && (
                          <span className="text-[10px] block font-bold text-amber-500 uppercase">
                            LOP Included (Absent days)
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 font-black text-gray-900 dark:text-white">
                        ₹{parseFloat(item.net_salary).toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest ${statusBadges[item.status]}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button
                          onClick={() => {
                            // Render a details popup alert or simple print details
                            const details = `
                              === Pay Slip Detail ===
                              Month: ${item.month}
                              Employee ID: ${item.employee}
                              
                              Earnings:
                              - Basic Salary: ₹${parseFloat(item.basic_salary).toLocaleString()}
                              - Sales Incentive: ₹${parseFloat(item.sales_incentive).toLocaleString()}
                              - Bonus: ₹${parseFloat(item.bonus).toLocaleString()}
                              - Travel Allowance: ₹${parseFloat(item.travel_allowance).toLocaleString()}
                              
                              Deductions:
                              - Provident Fund (PF): ₹${parseFloat(item.pf).toLocaleString()}
                              - Income Tax: ₹${parseFloat(item.tax).toLocaleString()}
                              - Loss of Pay (LOP): ₹${parseFloat(item.loss_of_pay).toLocaleString()}
                              
                              =======================
                              Net Deposited: ₹${parseFloat(item.net_salary).toLocaleString()}
                              Status: ${item.status}
                            `;
                            alert(details);
                          }}
                          className="text-xs font-bold text-green-600 hover:text-green-700 hover:underline"
                        >
                          View Breakup
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

function SlabRow({ label, rate, active }) {
  return (
    <div className={`flex justify-between items-center py-2 px-3 rounded-xl border ${
      active 
        ? "bg-green-50 border-green-200 text-green-600 dark:bg-green-950/30 dark:border-green-800 dark:text-green-400 font-bold" 
        : "bg-transparent border-transparent text-gray-400 font-medium"
    }`}>
      <span className="text-xs uppercase tracking-wide">{label}</span>
      <span className="text-xs font-black">{rate} Comm.</span>
    </div>
  );
}
